import { FC, memo } from 'react';
import { Content, asLink } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import SocialMediaLogo from '@/assets/logos/SocialMediaLogo';

/**
 * Props for `TeamMembersGrid`.
 */
export type TeamMembersGridProps =
  SliceComponentProps<Content.TeamMembersGridSlice>;

/**
 * Component for "TeamMembersGrid" Slices.
 */
const TeamMembersGrid: FC<TeamMembersGridProps> = memo(({ slice }) => {
  const {
    primary: { heading, description, members },
  } = slice;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-16 md:py-24"
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F423A 53.29%)',
      }}
    >
      <div className="container mx-auto px-6">
        {/* Header Section */}
        <div className="mb-16">
          {heading && (
            <div className="mb-6">
              <PrismicRichText
                field={heading}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}

          {description && (
            <div className="mx-auto">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="font-normal text-[18px] text-white leading-relaxed">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Team Members Grid */}
        {members && members.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {members.map((member, index) => {
              const isPriority = index < 3;

              return (
                <div key={index} className="text-center">
                  {/* Member Card - Square container encompassing entire card */}
                  <div
                    className="relative p-6 mx-auto rounded-2xl bg-gradient-to-b from-black/20 to-transparent flex flex-col items-center justify-center space-y-4"
                    style={{ minHeight: '280px', width: '200px' }}
                  >
                    {/* Circular Avatar */}
                    {member.photo && (
                      <div
                        className="flex-shrink-0 w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden"
                        style={
                          index === 0
                            ? {
                                border: '4px solid #05f273',
                              }
                            : undefined
                        }
                      >
                        <PrismicNextImage
                          field={member.photo}
                          className="w-full h-full object-cover object-top"
                          loading={isPriority ? 'eager' : 'lazy'}
                          fetchPriority={isPriority ? 'high' : undefined}
                          decoding={isPriority ? 'auto' : 'async'}
                          imgixParams={{
                            w: 256,
                            h: 256,
                            fit: 'crop',
                            auto: ['compress', 'format'],
                            q: 70,
                          }}
                          sizes="(max-width: 768px) 128px, 160px"
                        />
                      </div>
                    )}

                    {/* Member Info */}
                    <div className="text-white text-center flex-1 flex flex-col justify-center">
                      {member.name && (
                        <h3 className="text-lg font-semibold mb-1 leading-tight whitespace-pre-line">
                          {member.name}
                        </h3>
                      )}

                      {member.role && (
                        <p className="text-sm text-white/70 mb-1 whitespace-pre-line">
                          {member.role}
                        </p>
                      )}

                      {member.subtitle && (
                        <p className="text-xs text-white/60 mb-4">
                          {member.subtitle}
                        </p>
                      )}

                      {/* Social Links - Only show LinkedIn if available */}
                      {member.social_links &&
                        member.social_links.length > 0 && (
                          <div className="flex justify-center gap-2 mt-5">
                            {member.social_links.map((link, linkIndex) => {
                              // Get the URL from the link field
                              const linkUrl = asLink(link);
                              // Check if this is a LinkedIn link
                              if (linkUrl && linkUrl.includes('linkedin.com')) {
                                return (
                                  <a
                                    key={linkIndex}
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:opacity-80 transition-opacity duration-200"
                                  >
                                    <SocialMediaLogo
                                      platform="linkedin"
                                      className="w-8 h-8"
                                    />
                                  </a>
                                );
                              }
                              return null;
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
});

TeamMembersGrid.displayName = 'TeamMembersGrid';

export default TeamMembersGrid;
